<template>
  <div class="protocol-page">
    <div class="protocol-shell">
      <div class="protocol-header">
        <div class="protocol-brand">
          <img class="protocol-brand__logo" :src="brandLogoSrc" alt="iTex+" />
        </div>

        <div class="protocol-navigation" role="tablist" aria-label="Bereiche">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="protocol-tab"
            :class="{ 'protocol-tab--active': activeTab === tab.id }"
            :aria-selected="String(activeTab === tab.id)"
            @click="activeTab = tab.id"
          >
            <i class="mdi protocol-tab__icon" :class="tab.iconClass"></i>
            <span class="protocol-tab__label">{{ tab.label }}</span>
          </button>
        </div>

        <v-menu
          offset-y
          left
          attach=".protocol-shell"
          content-class="protocol-user-menu-content"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="protocol-user"
              v-bind="attrs"
              v-on="on"
            >
              <span class="protocol-user__avatar">
                {{ currentUserInitial }}
              </span>
              <span class="protocol-user__meta">
                <span class="protocol-user__name">{{ currentUserName }}</span>
              </span>
              <i class="mdi mdi-chevron-down protocol-user__chevron"></i>
            </button>
          </template>
          <v-list dense class="protocol-user-menu">
            <v-list-item @click="logout">
              <v-list-item-icon>
                <v-icon small color="red darken-1">mdi-logout-variant</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Abmelden</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <div class="card card-custom card-stretch gutter-b protocol-card">
        <!--begin::Body-->
        <div class="card-body d-flex flex-column protocol-card__body">
          <div class="flex-grow-1 pb-5">
            <!--begin::Info-->
            <v-row>
              <v-col cols="12" lg="9">
                <h1 style="text-transform: uppercase">
                  {{ activeSection.title }}
                </h1>
                <div class="d-flex align-items-center mb-6">
                  <span
                    class="text-muted font-weight-bold font-size-lg flex-grow-1"
                    >{{ activeSection.description }}</span
                  >
                </div>
              </v-col>
            </v-row>
            <!--end::Info-->

            <div class="vld-parent">
              <loading
                :active.sync="isLoading"
                :can-cancel="true"
                :is-full-page="true"
              ></loading>
            </div>

            <v-app>
              <v-form ref="form" v-model="valid" lazy-validation>
                <v-container fluid class="protocol-content mt-12 pa-0">
                  <v-row v-if="isBesuchsberichtTab" class="protocol-toolbar">
                    <v-col cols="12" class="d-flex align-center">
                      <div>Protokoll-Id: {{ protokoll.id }}</div>
                    </v-col>
                  </v-row>
                  <template v-if="isBesuchsberichtTab">
                    <!-- KUNDENAUSWAHL -->
                    <v-row>
                      <v-col class="d-flex" cols="12" sm="12">
                        <span class="font-weight-bold"
                          >AUSWAHL - KUNDEN / WÄSCHEREI</span
                        >
                      </v-col>
                      <v-col class="d-flex" cols="12" sm="12">
                        <v-autocomplete
                          class="ma-0 pa-0"
                          :items="customer"
                          :item-text="
                            item =>
                              `${item.company} ${
                                item.company2 ? '(' + item.company2 + ')' : ''
                              }`
                          "
                          label="Bitte wählen Sie einen Kunden"
                          @change="selectCustomer($event)"
                          return-object
                          attach=""
                          :rules="[v => !!v || 'Dies ist ein Pflichtfeld']"
                          required
                        >
                          <template v-slot:no-data>
                            <v-list-item>
                              <v-list-item-content>
                                <v-list-item-title
                                  >Keinen Kunden gefunden</v-list-item-title
                                >
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col
                        v-if="pendingCustomerSignatures.length"
                        cols="12"
                        class="pt-0"
                      >
                        <div
                          class="pending-signatures"
                          role="status"
                          aria-live="polite"
                        >
                          <div
                            class="pending-signatures__icon"
                            aria-hidden="true"
                          >
                            <i class="mdi mdi-signature-freehand"></i>
                          </div>
                          <div class="pending-signatures__content">
                            <div class="pending-signatures__title">
                              Kundenunterschrift ausstehend
                            </div>
                            <p class="pending-signatures__description">
                              {{ pendingCustomerSignatureDescription }}
                            </p>
                            <div class="pending-signatures__list">
                              <div
                                v-for="note in pendingCustomerSignatures"
                                :key="note.id"
                                class="pending-signatures__item"
                              >
                                <div class="pending-signatures__note">
                                  <span>Lieferschein</span>
                                  <strong>{{ note.note_number }}</strong>
                                  <span v-if="note.delivery_note_date">
                                    vom
                                    {{
                                      formatPendingDeliveryNoteDate(
                                        note.delivery_note_date
                                      )
                                    }}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  class="pending-signatures__link"
                                  :disabled="
                                    openingCustomerSignatureId === note.id
                                  "
                                  @click="openCustomerSignature(note)"
                                >
                                  <i
                                    class="mdi"
                                    :class="
                                      openingCustomerSignatureId === note.id
                                        ? 'mdi-loading mdi-spin'
                                        : 'mdi-open-in-new'
                                    "
                                    aria-hidden="true"
                                  ></i>
                                  <span>
                                    {{
                                      openingCustomerSignatureId === note.id
                                        ? "Wird geöffnet"
                                        : "Kunden unterschreiben lassen"
                                    }}
                                  </span>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </v-col>
                      <v-col class="d-flex" cols="12" sm="12">
                        <v-autocomplete
                          class="ma-0 pa-0"
                          :items="laundry"
                          item-text="name_laundry"
                          label="Bitte wählen Sie eine Wäscherei"
                          @change="selectLaundry($event)"
                          return-object
                          attach=""
                          :rules="[v => !!v || 'Dies ist ein Pflichtfeld']"
                          required
                        >
                          <template v-slot:no-data>
                            <v-list-item>
                              <v-list-item-content>
                                <v-list-item-title
                                  >Keine Wäscherei gefunden</v-list-item-title
                                >
                              </v-list-item-content>
                            </v-list-item>
                          </template>
                        </v-autocomplete>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="d-flex" cols="12" sm="12">
                        <v-text-field
                          class="ma-0 pa-0"
                          :value="
                            protokoll.email_address
                              ? protokoll.email_address +
                                (protokoll.email_address2
                                  ? ', ' + protokoll.email_address2
                                  : '')
                              : 'keine Empfänger hinterlegt'
                          "
                          :disabled="true"
                          label="Kunden- Empfänger werden automatisch befüllt"
                          attach=""
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="d-flex" cols="12" sm="12">
                        <v-text-field
                          class="ma-0 pa-0"
                          :value="
                            protokoll.email_laundry
                              ? protokoll.email_laundry
                              : 'Keine Wäscherei hinterlegt'
                          "
                          :disabled="true"
                          label="Wäscherei- Empfänger werden automatisch befüllt"
                          attach=""
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <!-- NOTIZEN -->
                    <v-row>
                      <v-col class="d-flex mt-1" cols="12" sm="12">
                        <span class="font-weight-bold"
                          >NOTIZEN (GESPEICHERT)</span
                        ><br />
                      </v-col>
                      <v-col class="d-flex" cols="12" sm="12">
                        <v-textarea
                          v-model="protokoll.notice_permanent"
                          name="input-7-1"
                          filled
                          auto-grow
                          rows="2"
                          max-rows="5"
                          label="Hinterlegen Sie hier optional Ihre Notizen. Diese sind permanent verfügbar."
                        ></v-textarea>
                      </v-col>
                    </v-row>
                    <!-- EMPFÄNGER -->
                    <!--            <v-row>
              <v-col class="d-flex" cols="12" sm="12">
                <span class="font-weight-bold">EMPFÄNGER</span><br />
              </v-col>
              <v-col class="d-flex" cols="12" sm="12">
                <v-text-field
                  class="ma-0 pa-0"
                  v-model="protokoll.additional_receiver"
                  label="Weiterer Empfänger (optional)"
                  hint="Sie haben optional die Möglichkeit zusätzlich einene weiteren Empfänger einzutragen"
                  attach=""
                ></v-text-field>
              </v-col>
            </v-row>-->
                    <!-- BESUCHSDATUM / NÄCHSTER BESUCH -->
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-row>
                          <v-col class="d-flex" cols="12">
                            <span class="font-weight-bold">BESUCHSDATUM</span
                            ><br />
                          </v-col>
                          <v-col class="d-flex" cols="12">
                            <v-menu
                              class="ma-0 pa-0"
                              ref="menu1"
                              :close-on-content-click="true"
                              transition="scale-transition"
                              offset-y
                              min-width="auto"
                              attach=""
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  :value="protokoll.visit_date | dateFormatter"
                                  label="Besuchsdatum"
                                  prepend-icon="mdi-calendar"
                                  readonly
                                  v-bind="attrs"
                                  v-on="on"
                                ></v-text-field>
                              </template>
                              <v-date-picker
                                v-model="protokoll.visit_date"
                                no-title
                                scrollable
                                locale="de-de"
                              >
                              </v-date-picker>
                            </v-menu>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row>
                          <v-col class="d-flex" cols="12">
                            <span class="font-weight-bold">NÄCHSTER BESUCH</span
                            ><br />
                          </v-col>
                          <v-col class="d-flex" cols="12">
                            <v-menu
                              class="ma-0 pa-0"
                              ref="menu"
                              :close-on-content-click="true"
                              transition="scale-transition"
                              offset-y
                              min-width="auto"
                              locale="de-de"
                              attach=""
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  :value="
                                    protokoll.next_visit_date
                                      | dateFormatterMonth
                                  "
                                  label="Monat"
                                  prepend-icon="mdi-calendar"
                                  locale="de-de"
                                  readonly
                                  v-bind="attrs"
                                  v-on="on"
                                  :rules="[
                                    v => !!v || 'Dies ist ein Pflichtfeld'
                                  ]"
                                  required
                                ></v-text-field>
                              </template>
                              <v-date-picker
                                v-model="protokoll.next_visit_date"
                                type="month"
                                no-title
                                scrollable
                                :first-day-of-week="1"
                                locale="de-de"
                              >
                              </v-date-picker>
                            </v-menu>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                    <!-- 4.	Qualität der Wäsche -->
                    <v-row>
                      <v-col class="d-flex" cols="12" sm="12">
                        <span class="font-weight-bold text-uppercase"
                          >Qualität der Wäsche</span
                        ><br />
                      </v-col>
                      <v-col class="d-flex" cols="12" sm="12">
                        <ExpansionItem
                          :accordion_data="this.protokoll.laundry_quality"
                          :protokoll_id="this.protokoll.id"
                          @update="updateQuality"
                        >
                        </ExpansionItem>
                      </v-col>
                    </v-row>
                    <!-- 6.	Wäscherei -->
                    <v-row>
                      <v-col class="d-flex mt-7" cols="12" sm="12">
                        <span class="font-weight-bold text-uppercase"
                          >Wäscherei</span
                        ><br />
                      </v-col>
                      <v-col class="d-flex" cols="12" sm="12">
                        <ExpansionItem
                          :accordion_data="this.protokoll.laundry"
                          :protokoll_id="this.protokoll.id"
                          @update="updateLaundry"
                        >
                        </ExpansionItem>
                      </v-col>
                    </v-row>
                    <!-- 7. Defekte Wäsche -->
                    <div v-if="this.products.length > 0">
                      <v-row>
                        <v-col class="d-flex mt-7" cols="12" sm="12">
                          <span class="font-weight-bold text-uppercase"
                            >Defekte Wäsche</span
                          ><br />
                        </v-col>
                        <v-col cols="12">
                          <v-combobox
                            v-model="selectedItem"
                            :items="products"
                            label="Wählen Sie einen Artikel"
                            item-text="name"
                            :clearable="false"
                            attach=""
                            @change="openQuantityDialog"
                          ></v-combobox>
                        </v-col>
                      </v-row>
                      <!-- Ausgewählte Artikel Liste -->
                      <v-row v-if="selectedDefekteItems.length">
                        <v-col cols="12">
                          <v-data-table
                            :headers="headers"
                            :items="selectedDefekteItems"
                            class="elevation-1"
                            hide-default-footer
                          >
                            <template v-slot:item.actions="{ item }">
                              <v-btn icon @click="removeItem(item)">
                                <v-icon color="red">mdi-delete</v-icon>
                              </v-btn>
                            </template>
                          </v-data-table>
                        </v-col>
                      </v-row>

                      <!-- Dialog für Menge -->
                      <v-dialog v-model="dialog" max-width="400px">
                        <v-card>
                          <v-card-title>
                            <span class="headline">Menge eingeben</span>
                          </v-card-title>
                          <v-card-text>
                            <v-text-field
                              v-model="itemQuantity"
                              label="Menge"
                              type="number"
                              min="1"
                            ></v-text-field>
                          </v-card-text>
                          <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                              class="mt-2"
                              color="grey"
                              elevation="3"
                              large
                              style="color: white;"
                              @click="dialog = false"
                              >Abbrechen
                            </v-btn>
                            <v-btn
                              class="mt-2"
                              color="green"
                              elevation="3"
                              large
                              style="color: white;"
                              @click="addItem"
                              >OK
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-dialog>
                    </div>

                    <!-- 8. Aufstockung -->
                    <div v-if="this.products.length > 0">
                      <v-row>
                        <v-col class="d-flex mt-7" cols="12" sm="12">
                          <span class="font-weight-bold text-uppercase"
                            >Aufstockung</span
                          ><br />
                        </v-col>
                        <v-col cols="12">
                          <v-combobox
                            v-model="selectedStockItem"
                            :items="products"
                            label="Wählen Sie einen Artikel zur Aufstockung"
                            item-text="name"
                            :clearable="false"
                            attach=""
                            @change="openStockQuantityDialog"
                          ></v-combobox>
                        </v-col>
                      </v-row>
                      <!-- Ausgewählte Aufstockungs-Artikel Liste -->
                      <v-row v-if="selectedAufstockungItems.length">
                        <v-col cols="12">
                          <v-data-table
                            :headers="headers"
                            :items="selectedAufstockungItems"
                            class="elevation-1"
                            hide-default-footer
                          >
                            <template v-slot:item.actions="{ item }">
                              <v-btn icon @click="removeStockItem(item)">
                                <v-icon color="red">mdi-delete</v-icon>
                              </v-btn>
                            </template>
                          </v-data-table>
                        </v-col>
                      </v-row>

                      <!-- Dialog für Aufstockungsmenge -->
                      <v-dialog v-model="stockDialog" max-width="400px">
                        <v-card>
                          <v-card-title>
                            <span class="headline">Menge eingeben</span>
                          </v-card-title>
                          <v-card-text>
                            <v-text-field
                              v-model="stockItemQuantity"
                              label="Menge"
                              type="number"
                              min="1"
                            ></v-text-field>
                          </v-card-text>
                          <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                              class="mt-2"
                              color="grey"
                              elevation="3"
                              large
                              style="color: white;"
                              @click="stockDialog = false"
                              >Abbrechen
                            </v-btn>
                            <v-btn
                              class="mt-2"
                              color="green"
                              elevation="3"
                              large
                              style="color: white;"
                              @click="addStockItem"
                              >OK
                            </v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-dialog>
                    </div>
                    <!-- NOTIZEN -->
                    <v-row>
                      <v-col class="d-flex mt-7" cols="12" sm="12">
                        <span class="font-weight-bold">NOTIZEN (TEMPORÄR)</span
                        ><br />
                      </v-col>
                      <v-col class="d-flex" cols="12" sm="12">
                        <v-textarea
                          v-model="protokoll.notice"
                          name="input-7-1"
                          filled
                          auto-grow
                          rows="2"
                          max-rows="5"
                          label="Hinterlegen Sie hier optional Ihre Notizen (temoprär) für den jetzigen Besuch"
                        ></v-textarea>
                      </v-col>
                    </v-row>
                    <!-- UNTERSCHRIFT -->
                    <v-row>
                      <v-col class="d-flex mt-2" cols="12" sm="12">
                        <span class="font-weight-bold">KUNDEN-UNTERSCHRIFT</span
                        ><br />
                      </v-col>
                      <v-col class="d-flex" cols="12" sm="12"></v-col>
                      <VueSignaturePad
                        id="signature"
                        width="400px"
                        height="150px"
                        ref="signaturePad"
                      />
                      <v-col class="d-flex" cols="12" sm="12">
                        <label
                          >Hiermit bestätige ich, dass ich den Besuchsbericht
                          geprüft und zur Kenntnis genommen habe.</label
                        >
                      </v-col>
                      <v-col class="" cols="12" sm="12">
                        <v-btn class="btn" @click="undo"
                          >Unterschrift zurücksetzen</v-btn
                        >
                      </v-col>
                    </v-row>
                    <!-- ABSENDEN BUTTON -->
                    <v-row>
                      <v-col class="d-flex offset-8" cols="4" sm="4">
                        <v-btn
                          class="mt-6"
                          block
                          color="green"
                          elevation="3"
                          x-large
                          style="color: white;"
                          @click="send"
                          >ABSENDEN
                        </v-btn>
                      </v-col>
                    </v-row>
                  </template>
                  <template v-else>
                    <DeliveryNotesOverview :customers="customer" />
                  </template>

                  <!--            <v-row >
              <v-col cols="12">
                <v-file-input
                    label="File input"
                    outlined
                    :disabled="true"
                    dense
                    capture="environment"
                    accept="image/*"
                ></v-file-input>
              </v-col>
            </v-row>-->
                </v-container>
              </v-form>
            </v-app>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style>
#signature {
  border: double 3px transparent;
  border-radius: 5px;
  background-image: linear-gradient(white, white),
    radial-gradient(circle at top left, green, #9f6274);
  background-origin: border-box;
  background-clip: content-box, border-box;
  margin-left: 12px;
}

.protocol-page {
  width: 100%;
}

.protocol-shell {
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
}

.protocol-header {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 16px;
  padding: 12px 18px;
  border: 1px solid #e6ebf2;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 12px 26px rgba(25, 39, 60, 0.05);
}

.protocol-brand {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}

.protocol-brand__logo {
  display: block;
  width: auto;
  height: 42px;
  object-fit: contain;
}

.protocol-navigation {
  display: flex;
  align-items: stretch;
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
}

.protocol-navigation::-webkit-scrollbar {
  display: none;
}

.protocol-tab {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex: 0 0 auto;
  min-height: 62px;
  padding: 14px 22px;
  border: 0;
  border-bottom: 3px solid transparent;
  border-radius: 0;
  border-bottom: 3px solid transparent;
  background: transparent;
  color: #5b6472;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  transition: color 0.2s ease, border-color 0.2s ease,
    background-color 0.2s ease;
}

.protocol-tab--active {
  color: #1fa344;
  border-bottom-color: #1fa344;
}

.protocol-tab--active .protocol-tab__icon {
  color: #1fa344;
}

.protocol-tab__icon {
  color: #b9c0ce;
  font-size: 22px;
  line-height: 1;
}

.protocol-tab__label {
  display: inline-flex;
  align-items: center;
  line-height: 1.2;
}

.protocol-user {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
  min-height: 48px;
  padding: 8px 10px 8px 8px;
  border: 1px solid #edf1f5;
  border-radius: 14px;
  background: #fbfcfe;
  color: #2d3443;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.protocol-user:hover {
  border-color: #dbe3ec;
  box-shadow: 0 10px 18px rgba(20, 34, 54, 0.06);
}

.protocol-user:focus {
  outline: none;
}

.protocol-user__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #53616f;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
}

.protocol-user__meta {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.protocol-user__name {
  overflow: hidden;
  max-width: 140px;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 15px;
  font-weight: 600;
}

.protocol-user__chevron {
  color: #8e97a6;
  font-size: 18px;
  line-height: 1;
}

.protocol-user-menu {
  min-width: 180px;
}

.protocol-user-menu-content {
  z-index: 12 !important;
}

.protocol-card {
  margin-bottom: 0 !important;
  border: 1px solid #e8ecf3;
  border-radius: 16px;
  box-shadow: 0 12px 24px rgba(31, 43, 64, 0.04) !important;
}

.protocol-card__body {
  padding: 32px 40px 40px;
}

.protocol-logo {
  width: 100%;
  max-width: 240px;
  margin-left: auto;
}

.protocol-content {
  width: 100%;
}

.protocol-toolbar {
  margin-bottom: 8px;
}

.protocol-toolbar__label {
  color: #6b7280;
  font-weight: 600;
}

.pending-signatures {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  padding: 18px 20px;
  border: 1px solid #f0d59b;
  border-radius: 14px;
  background: #fffaf0;
  color: #28354a;
}

.pending-signatures__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #fff0c9;
  color: #9b6800;
}

.pending-signatures__icon .mdi {
  font-size: 24px;
  line-height: 1;
}

.pending-signatures__content {
  flex: 1 1 auto;
  min-width: 0;
}

.pending-signatures__title {
  margin-bottom: 3px;
  font-size: 15px;
  font-weight: 700;
}

.pending-signatures__description {
  margin: 0 0 12px;
  color: #697386;
  font-size: 14px;
  line-height: 1.5;
}

.pending-signatures__list {
  overflow: hidden;
  border: 1px solid rgba(155, 104, 0, 0.16);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.72);
}

.pending-signatures__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 48px;
  padding: 9px 12px 9px 14px;
}

.pending-signatures__item + .pending-signatures__item {
  border-top: 1px solid rgba(155, 104, 0, 0.12);
}

.pending-signatures__note {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
  color: #697386;
  font-size: 13px;
}

.pending-signatures__note strong {
  color: #28354a;
  font-size: 14px;
}

.pending-signatures__link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  min-height: 34px;
  padding: 7px 11px;
  border: 1px solid #d8b45f;
  border-radius: 9px;
  background: #ffffff;
  color: #815700;
  font-size: 13px;
  font-weight: 700;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.pending-signatures__link:hover:not(:disabled) {
  border-color: #b9820d;
  background: #fff6df;
}

.pending-signatures__link:focus-visible {
  outline: 3px solid rgba(185, 130, 13, 0.2);
  outline-offset: 2px;
}

.pending-signatures__link:disabled {
  cursor: wait;
  opacity: 0.65;
}

.protocol-placeholder {
  border: 1px dashed #d8dfe9;
  border-radius: 18px;
  background: #fafbfd;
  padding: 30px 32px;
}

.protocol-placeholder__eyebrow {
  display: inline-block;
  margin-bottom: 12px;
  color: #1fa344;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.protocol-placeholder__title {
  margin-bottom: 14px;
  color: #2d3443;
  font-size: 28px;
  line-height: 1.2;
}

.protocol-placeholder__text {
  margin: 0;
  color: #7b8190;
  font-size: 16px;
  line-height: 1.7;
}

@media (max-width: 991px) {
  .protocol-header {
    flex-wrap: wrap;
    gap: 14px;
  }

  .protocol-navigation {
    order: 3;
    width: 100%;
  }

  .protocol-card__body {
    padding: 28px 24px 32px;
  }
}

@media (max-width: 767px) {
  .protocol-header {
    padding: 12px 14px;
  }

  .protocol-brand {
    width: 100%;
    justify-content: center;
  }

  .protocol-navigation {
    order: 2;
    width: 100%;
  }

  .protocol-tab {
    min-height: 54px;
    padding: 12px 16px;
  }

  .protocol-user {
    order: 3;
    width: 100%;
    justify-content: center;
  }

  .protocol-placeholder {
    padding: 24px;
  }

  .protocol-placeholder__title {
    font-size: 22px;
  }

  .pending-signatures {
    padding: 16px;
  }

  .pending-signatures__item {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }
}

.header-mobile-fixed .wrapper {
  padding-top: 5px;
}

.header-fixed.header-bottom-enabled .wrapper {
  padding-top: 5px;
}

.custom_button.theme--light.v-btn.v-btn--disabled {
  background-color: lightgray !important;
}

.content-wrapper,
.flex-row-fluid {
  padding: 5px;
}

.v-image__image--cover {
  background-size: initial;
}
</style>
<script>
import ApiService from "@/core/services/api.service";
import { getUserProfile, LOGOUT } from "@/core/services/store/auth.module";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import DeliveryNotesOverview from "./DeliveryNotesOverview";
import ExpansionItem from "./ExpansionItem";
import Item_Stub from "../../../stub/item_stub";
import Swal from "sweetalert2";
import moment from "moment";
import short from "short-uuid";

export default {
  name: "besuchsprotokoll",
  components: { DeliveryNotesOverview, ExpansionItem, Loading },
  data: function() {
    return {
      valid: true,
      activeTab: "besuchsbericht",
      tabs: [
        {
          id: "besuchsbericht",
          label: "Besuchsbericht",
          title: "Besuchsprotokoll",
          description:
            "Dieses Protokoll wird automatisiert an den Berichterstatter, dem Kunden, der Wäscherei und optional der Buchhaltung/dem Einkauf gesendet.",
          iconClass: "mdi-clipboard-text-outline"
        },
        {
          id: "lieferscheine",
          label: "Lieferscheine",
          title: "Lieferscheine",
          description:
            "Die Lieferscheine werden erstellt, intern vom Mitarbeiter unterschrieben, online vom Kunden gegengezeichnet und nach Abschluss automatisch an alle Beteiligten versendet.",
          iconClass: "mdi-file-document-outline"
        }
      ],
      loadingvariable: false,
      isLoading: false,
      currentUser: null,
      protokoll: {
        id: short.generate(),
        user_id: null,
        user: null,
        customer: null,
        customer_id: null,
        laundry_id: null,
        email_address: null,
        email_address2: null,
        email_laundry: null,
        receiver: [],
        additional_receiver: null,
        visit_date: new Date(
          Date.now() - new Date().getTimezoneOffset() * 60000
        )
          .toISOString()
          .substr(0, 10),
        next_visit_date: new Date(
          Date.now() - new Date().getTimezoneOffset() * 60000
        )
          .toISOString()
          .substr(0, 10),
        laundry_quality: [],
        laundry_office: "",
        laundry: [],
        notice: null,
        notice_permanent: null,
        selectedDefekteItems: null,
        selectedAufstockungItems: null,
        signaturePad: null
      },
      customer: [],
      laundry: [],
      laundry_quality_cat: ["Wäsche", "Frottee", "Tischwäsche ", "Matten"],
      laundry_cat: ["Anlieferung", "Container", "Fahrer"],
      // Defekte Wäsche
      selectedItem: null,
      itemQuantity: 1,
      dialog: false,
      products: [],
      selectedDefekteItems: [],
      // Aufstockung
      selectedStockItem: null,
      stockItemQuantity: 1,
      stockDialog: false,
      selectedAufstockungItems: [],
      pendingCustomerSignatures: [],
      pendingCustomerSignatureRequestId: 0,
      openingCustomerSignatureId: null,
      headers: [
        { text: "Artikel", value: "name" },
        { text: "Menge", value: "quantity" },
        { text: "Aktionen", value: "actions", sortable: false }
      ]
    };
  },
  created() {
    this.documents = [];
    this.loadingvariable = true;

    let currentUser = JSON.parse(getUserProfile());
    this.currentUser = currentUser;
    this.protokoll.user_id = currentUser.id_user;
    this.protokoll.user = currentUser;

    ApiService.query("protokoll/customer")
      .then(({ data }) => {
        this.loadingvariable = false;
        this.customer = data.customer;
        this.laundry = data.laundry;
      })
      .catch(() => {
        this.loadingvariable = false;
        this.$store
          .dispatch(LOGOUT)
          .then(() => this.$router.push({ name: "login" }));
      });

    this.laundry_quality_cat.forEach(categories => {
      let itsb = { ...Item_Stub };
      itsb.name = categories;
      this.protokoll.laundry_quality.push(itsb);
    });

    this.laundry_cat.forEach(laundry => {
      let itsb = { ...Item_Stub };
      itsb.name = laundry;
      itsb.checkbox_legung = null;
      itsb.checkbox_flecken = null;
      itsb.checkbox_defekte = null;
      itsb.checkbox_menge = null;
      itsb.checkbox_anderes = true;
      this.protokoll.laundry.push(itsb);
    });
  },
  mounted() {
    window.addEventListener("focus", this.refreshPendingCustomerSignatures);
  },
  beforeDestroy() {
    window.removeEventListener("focus", this.refreshPendingCustomerSignatures);
  },
  filters: {
    dateFormatter: function(value) {
      if (value) return moment(String(value)).format("DD.MM.YYYY");
    },
    dateFormatterMonth: function(value) {
      if (value)
        return moment(String(value))
          .lang("de")
          .format("MMMM YYYY");
    }
  },
  computed: {
    activeSection() {
      return this.tabs.find(tab => tab.id === this.activeTab) || this.tabs[0];
    },
    isBesuchsberichtTab() {
      return this.activeTab === "besuchsbericht";
    },
    brandLogoSrc() {
      return `${process.env.BASE_URL}media/logos/itex-logo.png`;
    },
    currentUserName() {
      if (!this.currentUser) {
        return "Benutzer";
      }

      return (
        this.currentUser.user_name ||
        this.currentUser.firstname ||
        this.currentUser.lastname ||
        "Benutzer"
      );
    },
    currentUserInitial() {
      return this.currentUserName.charAt(0).toUpperCase();
    },
    pendingCustomerSignatureDescription() {
      const count = this.pendingCustomerSignatures.length;

      return count === 1
        ? "Für diesen Kunden wartet ein Lieferschein auf die Unterschrift."
        : `Für diesen Kunden warten ${count} Lieferscheine auf die Unterschrift.`;
    }
  },
  methods: {
    undo() {
      this.$refs.signaturePad.undoSignature();
    },
    selectLaundry(data) {
      this.protokoll.laundry_id = data.id_laundry;
      this.protokoll.laundry_office = data.name_laundry;
      this.protokoll.email_laundry = data.email_laundry;
    },
    selectCustomer(data) {
      this.pendingCustomerSignatures = [];
      this.pendingCustomerSignatureRequestId += 1;

      if (!data) {
        this.protokoll.customer_id = null;
        this.protokoll.customer = null;
        this.protokoll.email_address = null;
        this.protokoll.email_address2 = null;
        return;
      }

      this.protokoll.customer_id = data.id_customer;
      this.protokoll.customer = data;
      this.protokoll.email_address = data.email_address;
      this.protokoll.email_address2 = data.email_address2;
      this.protokoll.notice_permanent = "";

      this.fetchPendingCustomerSignatures(data.id_customer);

      this.isLoading = true;

      ApiService.get("protokoll/product", data.id_customer)
        .then(response => {
          if (response.data.products && response.data.products.length) {
            // If products exist, populate the items
            this.products = response.data.products;
          } else {
            this.products = [];
            // If no products found, show the message from the backend
            Swal.fire({
              title: "Info - Keine Artikel gefunden",
              text:
                response.data.message ||
                "Defekte Wäsche und Aufstockungen können daher nicht bearbeitet werden",
              icon: "info",
              showCancelButton: false,
              confirmButtonText: "Ok"
            });
          }
          this.isLoading = false;
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.log(error);
          this.isLoading = false;

          // Fallback error message for failed requests
          Swal.fire({
            title: "Fehler",
            text: "Es ist ein Fehler aufgetreten.",
            icon: "error",
            showCancelButton: false,
            confirmButtonText: "Ok"
          });
        });

      ApiService.get("protokoll/notice", data.id_customer)
        .then(response => {
          if (response.data.notice) {
            this.protokoll.notice_permanent = response.data.notice;
          }
          this.isLoading = false;
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.log(error);
          this.isLoading = false;

          // Fallback error message for failed requests
          Swal.fire({
            title: "Fehler",
            text:
              "Notizen konnten nicht abgerufen werden. Bitte kontaktieren Sie den Administrator.",
            icon: "error",
            showCancelButton: false,
            confirmButtonText: "Ok"
          });
        });
    },
    fetchPendingCustomerSignatures(customerId) {
      if (!customerId) {
        this.pendingCustomerSignatures = [];
        return Promise.resolve();
      }

      const requestId = ++this.pendingCustomerSignatureRequestId;

      return ApiService.get(
        "delivery-notes/pending-customer-signatures",
        customerId
      )
        .then(({ data }) => {
          if (
            requestId !== this.pendingCustomerSignatureRequestId ||
            String(this.protokoll.customer_id) !== String(customerId)
          ) {
            return;
          }

          this.pendingCustomerSignatures = Array.isArray(data.delivery_notes)
            ? data.delivery_notes
            : [];
        })
        .catch(error => {
          if (requestId === this.pendingCustomerSignatureRequestId) {
            this.pendingCustomerSignatures = [];
          }

          // eslint-disable-next-line no-console
          console.log(error);
        });
    },
    refreshPendingCustomerSignatures() {
      if (this.isBesuchsberichtTab && this.protokoll.customer_id) {
        this.fetchPendingCustomerSignatures(this.protokoll.customer_id);
      }
    },
    formatPendingDeliveryNoteDate(value) {
      return moment(String(value)).format("DD.MM.YYYY");
    },
    openCustomerSignature(note) {
      if (!note || this.openingCustomerSignatureId !== null) {
        return;
      }

      const signingWindow = window.open("about:blank", "_blank");

      if (signingWindow) {
        signingWindow.opener = null;
        signingWindow.document.title = "Signaturseite wird geladen";
        signingWindow.document.body.innerHTML =
          '<p style="font:16px sans-serif;padding:32px;color:#28354a">Signaturseite wird geladen ...</p>';
      }

      this.openingCustomerSignatureId = note.id;

      ApiService.post(`delivery-notes/${note.id}/customer-sign-url`, {})
        .then(({ data }) => {
          if (!data.sign_url) {
            throw new Error("Keine Signatur-URL erhalten.");
          }

          if (signingWindow && !signingWindow.closed) {
            signingWindow.location.replace(data.sign_url);
            return;
          }

          Swal.fire({
            title: "Signaturseite öffnen",
            text:
              "Das neue Fenster wurde vom Browser blockiert. Möchten Sie die Signaturseite jetzt öffnen?",
            icon: "info",
            showCancelButton: true,
            confirmButtonText: "Jetzt öffnen",
            cancelButtonText: "Abbrechen"
          }).then(result => {
            if (result.isConfirmed || result.value) {
              window.open(data.sign_url, "_blank", "noopener,noreferrer");
            }
          });
        })
        .catch(() => {
          if (signingWindow && !signingWindow.closed) {
            signingWindow.close();
          }

          this.fetchPendingCustomerSignatures(this.protokoll.customer_id);

          Swal.fire({
            title: "Link nicht verfügbar",
            text:
              "Der Lieferschein ist möglicherweise bereits unterschrieben. Bitte aktualisieren Sie die Übersicht und versuchen Sie es erneut.",
            icon: "info",
            showCancelButton: false,
            confirmButtonText: "Ok"
          });
        })
        .finally(() => {
          this.openingCustomerSignatureId = null;
        });
    },
    updateQuality(data) {
      if (data) this.protokoll.laundry_quality = data.accordion_data;
    },
    updateLaundry(data) {
      if (data) this.protokoll.laundry = data.accordion_data;
    },
    sendTest() {
      this.isLoading = true;

      setTimeout(() => {
        this.isLoading = false;
        // Remove page loader after some time
        Swal.fire({
          title: "Erfolgreich übermittelt",
          text: "Das Besuchsprotokoll wurde erfolgreich übermittelt.",
          icon: "success",
          showCancelButton: false,
          confirmButtonText: "Ok"
        });
      }, 3000);
    },
    send() {
      this.protokoll.selectedDefekteItems = this.selectedDefekteItems;
      this.protokoll.selectedAufstockungItems = this.selectedAufstockungItems;

      const { data } = this.$refs.signaturePad.saveSignature();
      this.protokoll.signaturePad = data;

      if (this.$refs.form.validate()) {
        this.isLoading = true;

        ApiService.post("protokoll/send", this.protokoll)
          .then(data => {
            this.isLoading = false;
            if (data.status === 201) {
              Swal.fire({
                title: "Erfolgreich übermittelt",
                text:
                  "Der Besuchsbericht wurde erfolgreich übermittelt und wird nun verarbeitet.",
                icon: "success",
                showCancelButton: false,
                confirmButtonText: "Ok"
              }).then(okay => {
                if (okay) window.location.reload();
              });
            }
          })
          .catch(({ response }) => {
            if (response.status === 401) {
              if (response.status === 401) {
                this.loadingvariable = false;
                this.$store
                  .dispatch(LOGOUT)
                  .then(() => this.$router.push({ name: "login" }));
              }
            } else {
              Swal.fire({
                title: "Fehler",
                text: "Beim Versenden des Berichtes ist ein Fehler aufgetreten",
                icon: "error",
                showCancelButton: false,
                confirmButtonText: "Ok"
              });
            }
          });
      } else {
        Swal.fire({
          title: "Warnung",
          text: "Bitte füllen Sie alle Pflichtfelder aus",
          icon: "warning",
          showCancelButton: false,
          confirmButtonText: "Ok"
        });
      }
    },
    logout() {
      this.$store
        .dispatch(LOGOUT)
        .then(() => this.$router.push({ name: "login" }));
    },
    // Defekte Wäsche Funktionen
    openQuantityDialog() {
      if (this.selectedItem) {
        this.dialog = true;
      }
    },
    addItem() {
      if (this.selectedItem && this.itemQuantity > 0) {
        // Entferne alten Eintrag, wenn vorhanden
        this.selectedDefekteItems = this.selectedDefekteItems.filter(
          item => item.id !== this.selectedItem.id
        );

        // Füge neuen Eintrag mit aktueller Menge hinzu
        this.selectedDefekteItems.push({
          ...this.selectedItem,
          quantity: this.itemQuantity
        });

        this.selectedItem = null;
        this.itemQuantity = 1;
        this.dialog = false;
      }
    },
    removeItem(item) {
      this.selectedDefekteItems = this.selectedDefekteItems.filter(
        i => i.id !== item.id
      );
    },
    // Aufstockung Funktionen
    openStockQuantityDialog() {
      if (this.selectedStockItem) {
        this.stockDialog = true;
      }
    },
    addStockItem() {
      if (this.selectedStockItem && this.stockItemQuantity > 0) {
        // Entferne alten Eintrag, wenn vorhanden
        this.selectedAufstockungItems = this.selectedAufstockungItems.filter(
          item => item.id !== this.selectedStockItem.id
        );

        // Füge neuen Eintrag hinzu (immer)
        this.selectedAufstockungItems.push({
          id: this.selectedStockItem.id,
          internal_item_id:
            this.selectedStockItem.internal_item_id ||
            this.selectedStockItem.item_id ||
            this.selectedStockItem.id,
          name: this.selectedStockItem.name,
          external_item_id: this.selectedStockItem.external_item_id,
          quantity: this.stockItemQuantity
        });

        this.selectedStockItem = null;
        this.stockItemQuantity = 1;
        this.stockDialog = false;
      }
    },
    removeStockItem(item) {
      this.selectedAufstockungItems = this.selectedAufstockungItems.filter(
        i => i.id !== item.id
      );
    }
  }
};
</script>
